// src/pages/admin/AdminProfile.jsx - COMPLETE FIXED VERSION
import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Button,
  TextField,
  Stack,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
  Badge,
} from "@mui/material";
import {
  Edit,
  Save,
  Cancel,
  PhotoCamera,
  AdminPanelSettings,
  Email,
  Phone,
  LocationOn,
  Verified,
  Security,
  Person,
  Shield,
  Badge as BadgeIcon,
  ContactMail,
} from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import { adminApi } from "../../api/adminApi";

const AdminProfile = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { user, updateUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
  });

  const userAvatarUrl = user?.profileImage || user?.avatar || null;
  const displayAvatarUrl = previewImage || userAvatarUrl;

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        address: user.address || "",
      });
    }
  }, [user]);

  const handleChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value,
    });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadAvatar = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('avatar', selectedFile);
      
      const response = await adminApi.updateAdminAvatar(formData);
      
      if (response && response.user) {
        updateUser(response.user);
      }
      
      setOpenImageDialog(false);
      setSelectedFile(null);
      setPreviewImage(null);
      setSuccess('Profile picture updated successfully!');
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to upload avatar');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const saveData = {
        name: profileData.name,
        email: profileData.email,
        phone: profileData.phone,
        address: profileData.address,
      };
      
      const response = await adminApi.updateAdminProfile(saveData);
      
      if (response && response.user) {
        updateUser(response.user);
      }
      
      setEditing(false);
      setSuccess("Profile updated successfully!");
    } catch (error) {
      setError(error.response?.data?.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setProfileData({
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      address: user.address || "",
    });
    setEditing(false);
    setError(null);
    setSuccess(null);
  };

  const getAvatarLetter = () => {
    if (user?.name) return user.name[0].toUpperCase();
    return 'A';
  };

  const getRoleColor = (role) => {
    const colors = {
      super_admin: 'error',
      admin: 'primary',
      manager: 'warning',
      support: 'info',
    };
    return colors[role] || 'default';
  };

  const getRoleDisplay = (role) => {
    const roles = {
      super_admin: 'Super Administrator',
      admin: 'Administrator',
      manager: 'Manager',
      support: 'Support Staff',
    };
    return roles[role] || role || 'Administrator';
  };

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 8, sm: 10 } }}>
      {/* Header */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              Admin Profile
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your administrator account settings
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} width={{ xs: '100%', sm: 'auto' }}>
            {editing ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={handleCancel}
                  disabled={loading}
                  size={isMobile ? 'small' : 'medium'}
                  fullWidth={isMobile}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSave}
                  disabled={loading}
                  size={isMobile ? 'small' : 'medium'}
                  fullWidth={isMobile}
                >
                  {loading ? "Saving..." : "Save Changes"}
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={() => setEditing(true)}
                size={isMobile ? 'small' : 'medium'}
                fullWidth={isMobile}
              >
                Edit Profile
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Profile Header Card */}
      <Paper sx={{ 
        p: { xs: 2, sm: 3 }, 
        mb: 3, 
        borderRadius: 3, 
        textAlign: "center",
      }}>
        <Stack alignItems="center" spacing={2}>
          <Box sx={{ position: "relative" }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <IconButton
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    width: { xs: 32, sm: 40 },
                    height: { xs: 32, sm: 40 },
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                  onClick={() => setOpenImageDialog(true)}
                  size={isMobile ? 'small' : 'medium'}
                >
                  <PhotoCamera sx={{ fontSize: { xs: 16, sm: 20 } }} />
                </IconButton>
              }
            >
              <Avatar
                sx={{
                  width: { xs: 100, sm: 120 },
                  height: { xs: 100, sm: 120 },
                  bgcolor: displayAvatarUrl ? 'transparent' : 'primary.main',
                  fontSize: { xs: 36, sm: 48 },
                  border: '3px solid',
                  borderColor: 'background.paper',
                  boxShadow: theme.shadows[3],
                }}
                src={displayAvatarUrl}
              >
                {!displayAvatarUrl && getAvatarLetter()}
              </Avatar>
            </Badge>
          </Box>
          <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
            {user.name || "Admin User"}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
            <Chip
              icon={<Shield />}
              label={getRoleDisplay(user.role)}
              color={getRoleColor(user.role)}
              sx={{ fontWeight: 500 }}
            />
            <Chip icon={<Verified />} label="Verified Account" color="success" />
            {user.isActive !== undefined && (
              <Chip
                icon={user.isActive ? <Verified /> : <Security />}
                label={user.isActive ? "Active" : "Inactive"}
                color={user.isActive ? "success" : "default"}
              />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AdminPanelSettings sx={{ fontSize: 16 }} />
            Administrator since {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
          </Typography>
        </Stack>
      </Paper>

      {/* Profile Details */}
      <Grid container spacing={isMobile ? 2 : 3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                Personal Information
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Full Name
                  </Typography>
                  {editing ? (
                    <TextField
                      fullWidth
                      name="name"
                      value={profileData.name}
                      onChange={handleChange}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1" fontWeight="500">
                      {user.name || "Not set"}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Email Address
                  </Typography>
                  {editing ? (
                    <TextField
                      fullWidth
                      name="email"
                      value={profileData.email}
                      onChange={handleChange}
                      size="small"
                      sx={{ mt: 0.5 }}
                      type="email"
                    />
                  ) : (
                    <Typography variant="body1">
                      {user.email || "Not set"}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                <ContactMail sx={{ mr: 1, verticalAlign: 'middle' }} />
                Contact & Location
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Phone Number
                  </Typography>
                  {editing ? (
                    <TextField
                      fullWidth
                      name="phone"
                      value={profileData.phone}
                      onChange={handleChange}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1">
                      {user.phone || "Not set"}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Address
                  </Typography>
                  {editing ? (
                    <TextField
                      fullWidth
                      name="address"
                      value={profileData.address}
                      onChange={handleChange}
                      size="small"
                      sx={{ mt: 0.5 }}
                      multiline
                      rows={2}
                    />
                  ) : (
                    <Typography variant="body1">
                      {user.address || "Not set"}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Admin Statistics Card */}
        <Grid item xs={12}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              <BadgeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Admin Access & Permissions
            </Typography>
            <Grid container spacing={isMobile ? 2 : 3}>
              <Grid item xs={6} sm={3}>
                <Stack alignItems="center" sx={{ p: 1 }}>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="primary">
                    {user.role === 'super_admin' ? 'Full' : user.role === 'admin' ? 'High' : 'Standard'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Access Level</Typography>
                </Stack>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Stack alignItems="center" sx={{ p: 1 }}>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="primary">
                    {user.permissions?.length || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Permissions</Typography>
                </Stack>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Stack alignItems="center" sx={{ p: 1 }}>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="primary">
                    {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'N/A'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Last Login</Typography>
                </Stack>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Stack alignItems="center" sx={{ p: 1 }}>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="primary">
                    {user.loginCount || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Total Logins</Typography>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Update Avatar Dialog */}
      <Dialog open={openImageDialog} onClose={() => setOpenImageDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Update Profile Picture</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Avatar
              sx={{
                width: { xs: 100, sm: 120 },
                height: { xs: 100, sm: 120 },
                mx: 'auto',
                mb: 2,
                bgcolor: 'primary.main',
              }}
              src={displayAvatarUrl}
            >
              {!displayAvatarUrl && getAvatarLetter()}
            </Avatar>
            <Button variant="outlined" component="label">
              Choose New Photo
              <input type="file" hidden accept="image/*" onChange={handleFileSelect} />
            </Button>
            {selectedFile && (
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                {selectedFile.name}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setOpenImageDialog(false)} fullWidth={isMobile}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUploadAvatar}
            disabled={!selectedFile || uploading}
            fullWidth={isMobile}
          >
            {uploading ? <CircularProgress size={24} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminProfile;